"use client";

import { useTranslations } from "next-intl";
import {
  Accessibility,
  Armchair,
  Car,
  Clock,
  Coffee,
  MapPin,
  MonitorCheck,
  Phone,
  Plane,
  Stamp,
  Wifi,
} from "lucide-react";
import { RegionalLayout } from "@/components/RegionalLayout";
import {
  RegionalHero,
  RegionalInfoStrip,
  SectionHeader,
} from "@/components/RegionalHome";
import { mppaConfig } from "./config";

// Imágenes para el carrusel del hero
const CAROUSEL_IMAGES = [
  "https://galeria.tocumenaero.com/gallery/regionales/MPPA.JPG",
  "https://galeria.tocumenaero.com/gallery/regionales/MPPA2.JPG",
  "https://galeria.tocumenaero.com/gallery/api/Aeropuerto%20Panam%C3%A1%20Pac%C3%ADfico/9M1A6595.jpg",
];

const SERVICE_ICONS = [
  Plane,
  MonitorCheck,
  Stamp,
  Armchair,
  Wifi,
  Accessibility,
] as const;

type Airline = {
  name: string;
  badge: string;
  countries: string;
  destinations: string[];
};

type Service = {
  title: string;
  desc: string;
};

export default function MppaPage() {
  const t = useTranslations("mppa");

  const airlines = t.raw("aerolineas.items") as Airline[];
  const services = t.raw("servicios.items") as Service[];
  const rentacarBrands = t.raw("instalaciones.rentacar.brands") as string[];

  const infoItems = [
    {
      icon: MapPin,
      label: t("info.ubicacionLabel"),
      value: t("info.ubicacionValue"),
    },
    {
      icon: Clock,
      label: t("info.horarioLabel"),
      value: t("info.horarioValue"),
    },
    {
      icon: Phone,
      label: t("info.telefonoLabel"),
      value: mppaConfig.contact.phone,
      href: `tel:${mppaConfig.contact.phone}`,
    },
    {
      icon: Wifi,
      label: t("info.wifiLabel"),
      value: t("info.wifiValue"),
    },
  ];

  return (
    <RegionalLayout config={mppaConfig}>
      <div className="bg-white">
        <RegionalHero
          images={CAROUSEL_IMAGES}
          eyebrow={t("hero.eyebrow")}
          title={t("hero.title")}
          subtitle={t("hero.subtitle")}
        />

        <RegionalInfoStrip items={infoItems} />

        {/* Aerolíneas y destinos */}
        <section className="py-20 border-t border-gray-200">
          <div className="max-w-[80%] mx-auto">
            <SectionHeader
              eyebrow={t("aerolineas.eyebrow")}
              title={t("aerolineas.title")}
              subtitle={t("aerolineas.subtitle")}
            />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200">
              {airlines.map((airline) => (
                <div
                  key={airline.name}
                  className="bg-white p-8 transition-colors hover:bg-gray-50"
                >
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <h3 className="text-2xl font-bold text-gray-900 tracking-tight">
                      {airline.name}
                    </h3>
                    <span className="shrink-0 px-3 py-1 rounded-full border border-gray-300 text-xs uppercase tracking-wider font-semibold text-[#102542]">
                      {airline.badge}
                    </span>
                  </div>
                  <p className="text-xs font-mono uppercase tracking-wider text-gray-400 mb-6">
                    {airline.countries}
                  </p>
                  <p className="text-xs font-mono uppercase tracking-wider text-gray-400 mb-3">
                    {t("aerolineas.destinosLabel")}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {airline.destinations.map((destination) => (
                      <span
                        key={destination}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-300 text-sm text-gray-700 transition-colors hover:border-[#102542]"
                      >
                        <Plane className="w-3.5 h-3.5 text-[#102542]" />
                        {destination}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Servicios de la terminal */}
        <section className="py-20 border-t border-gray-200">
          <div className="max-w-[80%] mx-auto">
            <SectionHeader
              eyebrow={t("servicios.eyebrow")}
              title={t("servicios.title")}
              subtitle={t("servicios.subtitle")}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200">
              {services.map((service, index) => {
                const Icon = SERVICE_ICONS[index] ?? Plane;
                return (
                  <div
                    key={service.title}
                    className="bg-white p-8 transition-colors hover:bg-gray-50"
                  >
                    <Icon className="w-6 h-6 text-[#102542] mb-4" />
                    <h3 className="font-bold text-gray-900 tracking-tight mb-2">
                      {service.title}
                    </h3>
                    <p className="text-sm text-gray-500 leading-relaxed">
                      {service.desc}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Instalaciones y comodidades */}
        <section className="py-20 border-t border-gray-200">
          <div className="max-w-[80%] mx-auto">
            <SectionHeader
              eyebrow={t("instalaciones.eyebrow")}
              title={t("instalaciones.title")}
              subtitle={t("instalaciones.subtitle")}
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200">
              {/* Estacionamiento */}
              <div className="bg-white p-8 transition-colors hover:bg-gray-50">
                <div className="flex items-start justify-between gap-4 mb-6">
                  <Car className="w-6 h-6 text-[#102542]" />
                  <span className="px-3 py-1 rounded-full border border-gray-300 text-xs uppercase tracking-wider font-semibold text-[#102542]">
                    {t("instalaciones.estacionamiento.badge")}
                  </span>
                </div>
                <p className="text-4xl font-bold text-[#102542] tracking-tight tabular-nums mb-1">
                  {t("instalaciones.estacionamiento.metric")}
                </p>
                <p className="text-xs font-mono uppercase tracking-wider text-gray-400 mb-4">
                  {t("instalaciones.estacionamiento.metricLabel")}
                </p>
                <h3 className="font-bold text-gray-900 tracking-tight mb-2">
                  {t("instalaciones.estacionamiento.title")}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                  {t("instalaciones.estacionamiento.desc")}
                </p>
              </div>

              {/* Renta de autos */}
              <div className="bg-white p-8 transition-colors hover:bg-gray-50">
                <Car className="w-6 h-6 text-[#102542] mb-6" />
                <p className="text-4xl font-bold text-[#102542] tracking-tight tabular-nums mb-1">
                  {t("instalaciones.rentacar.metric")}
                </p>
                <p className="text-xs font-mono uppercase tracking-wider text-gray-400 mb-4">
                  {t("instalaciones.rentacar.metricLabel")}
                </p>
                <h3 className="font-bold text-gray-900 tracking-tight mb-2">
                  {t("instalaciones.rentacar.title")}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed mb-4">
                  {t("instalaciones.rentacar.desc")}
                </p>
                <div className="flex flex-wrap gap-2">
                  {rentacarBrands.map((brand) => (
                    <span
                      key={brand}
                      className="px-3 py-1 rounded-full border border-gray-300 text-xs text-gray-700 transition-colors hover:border-[#102542]"
                    >
                      {brand}
                    </span>
                  ))}
                </div>
              </div>

              {/* Cafetería */}
              <div className="bg-white p-8 transition-colors hover:bg-gray-50">
                <div className="flex items-start justify-between gap-4 mb-6">
                  <Coffee className="w-6 h-6 text-[#102542]" />
                  <span className="px-3 py-1 rounded-full border border-gray-300 text-xs uppercase tracking-wider font-semibold text-[#102542]">
                    {t("instalaciones.cafeteria.badge")}
                  </span>
                </div>
                <p className="text-4xl font-bold text-[#102542] tracking-tight tabular-nums mb-1">
                  {t("instalaciones.cafeteria.metric")}
                </p>
                <p className="text-xs font-mono uppercase tracking-wider text-gray-400 mb-4">
                  {t("instalaciones.cafeteria.metricLabel")}
                </p>
                <h3 className="font-bold text-gray-900 tracking-tight mb-2">
                  {t("instalaciones.cafeteria.title")}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                  {t("instalaciones.cafeteria.desc")}
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </RegionalLayout>
  );
}
